/**
 * KeyboardShortcuts — глобальные клавиатурные шорткаты в vim-стиле.
 *
 * Поддерживает:
 *   - "g s" → /scanner, "g t" → / (Terminal), "g r" → /routes,
 *     "g a" → /alerts, "g i" → /infinity,
 *     "g f" → /funding, "g k" → /api-keys, "g h" → /profile
 *   - "?" → открыть оверлей со списком всех шорткатов
 *   - "t" → toggle theme (быстрая смена темы без клика по иконке)
 *   - Esc внутри оверлея — закрыть
 *
 * Шорткаты игнорируются если фокус в input/textarea/select/contentEditable —
 * иначе печатать в поля стало бы невозможно.
 *
 * Cmd+K палитра живёт отдельно (CommandPalette.tsx) и продолжает работать
 * независимо — здесь только лёгкая навигация без модалок.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Rocket, Repeat, BellRing,
  Activity, KeyRound, UserCircle, Command, Sun, X,
  Keyboard,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useThemeStore } from '../store/useThemeStore';

// Lucide icons имеют свой widened LucideProps тип, несовместимый с узким
// ComponentType — используем any-icon shape, как в CommandPalette.
type IconType = React.FC<{ size?: number | string; className?: string }>;

interface Shortcut {
  keys: string[];               // комбо для отображения, например ['g', 's']
  description: string;
  icon?: IconType;
  group: 'nav' | 'actions' | 'global';   // идентификатор; подпись — groups.<id> в словаре
}

const SHORTCUTS: Shortcut[] = [
  { keys: ['g', 't'], description: i18n.t('keyboardShortcuts:items.terminal'), icon: Rocket,       group: 'nav' },
  { keys: ['g', 's'], description: i18n.t('keyboardShortcuts:items.scanner'),  icon: Search,       group: 'nav' },
  { keys: ['g', 'i'], description: i18n.t('keyboardShortcuts:items.auto'),     icon: Repeat,       group: 'nav' },
  { keys: ['g', 'a'], description: i18n.t('keyboardShortcuts:items.alerts'),   icon: BellRing,     group: 'nav' },
  { keys: ['g', 'f'], description: i18n.t('keyboardShortcuts:items.funding'),  icon: Activity,     group: 'nav' },
  { keys: ['g', 'k'], description: i18n.t('keyboardShortcuts:items.apiKeys'),  icon: KeyRound,     group: 'nav' },
  { keys: ['g', 'h'], description: i18n.t('keyboardShortcuts:items.profile'),  icon: UserCircle,   group: 'nav' },
  { keys: ['t'],      description: i18n.t('keyboardShortcuts:items.theme'),    icon: Sun,          group: 'actions' },
  { keys: ['⌘', 'K'], description: i18n.t('keyboardShortcuts:items.search'),   icon: Command,      group: 'global' },
  { keys: ['?'],      description: i18n.t('keyboardShortcuts:items.help'),     icon: Keyboard,     group: 'global' },
];

const NAV_MAP: Record<string, string> = {
  t: '/',
  s: '/scanner',
  i: '/auto',
  a: '/alerts',
  f: '/funding',
  k: '/api-keys',
  h: '/profile',
};

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

export const KeyboardShortcuts: React.FC = () => {
  const { t } = useTranslation('keyboardShortcuts');
  const nav = useNavigate();
  const toggleTheme = useThemeStore(s => s.toggle);
  const [helpOpen, setHelpOpen] = useState(false);
  // флаг ожидания второй клавиши после "g"
  const awaitingG = useRef(false);
  const gTimer = useRef<number | null>(null);
  const [gHint, setGHint] = useState(false);

  useEffect(() => {
    const clearG = () => {
      awaitingG.current = false;
      setGHint(false);
      if (gTimer.current) { window.clearTimeout(gTimer.current); gTimer.current = null; }
    };

    const onKey = (e: KeyboardEvent) => {
      // Не перехватываем шорткаты во время ввода в поля.
      if (isTypingTarget(e.target)) return;
      // Cmd/Ctrl/Alt комбинации оставляем браузеру и Cmd+K палитре.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // Esc внутри overlay'я
      if (key === 'escape' && helpOpen) {
        e.preventDefault();
        setHelpOpen(false);
        clearG();
        return;
      }

      // ? — открыть help
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(o => !o);
        clearG();
        return;
      }

      // не реагируем пока help открыт (кроме ? и Esc выше)
      if (helpOpen) return;

      // "t" в одиночку = toggle theme. Но если только что нажали g — это
      // часть комбо "g t" → Terminal.
      if (awaitingG.current) {
        const target = NAV_MAP[key];
        if (target) {
          e.preventDefault();
          nav(target);
        }
        clearG();
        return;
      }

      if (key === 'g') {
        awaitingG.current = true;
        setGHint(true);
        gTimer.current = window.setTimeout(clearG, 1500);
        return;
      }

      if (key === 't') {
        e.preventDefault();
        toggleTheme();
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (gTimer.current) window.clearTimeout(gTimer.current);
    };
  }, [helpOpen, nav, toggleTheme]);

  return (
    <>
      {/* Подсказка-чип "g ?" пока ждём вторую клавишу — в углу, не мешает */}
      {gHint && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 lg:bottom-6 z-[90]
                        pointer-events-none animate-sb-fade-slide-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-popover/95 px-3 py-1.5
                          shadow-lg backdrop-blur text-[12px] text-foreground">
            <Keyboard className="h-3.5 w-3.5 text-primary"/>
            <span className="font-mono">g</span>
            <span className="text-muted-foreground">+ s/t/r/a/i/p/f/k/h…</span>
            <span className="text-[10px] text-muted-foreground ml-1">{t('hint.orAll')}</span>
          </div>
        </div>
      )}

      {/* Help overlay */}
      {helpOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
             onClick={() => setHelpOpen(false)}>
          <div className="w-full max-w-xl max-h-[80vh] overflow-hidden rounded-xl border border-border bg-popover shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <Keyboard className="h-4 w-4 text-primary"/>
                <h2 className="text-sm font-semibold">{t('title')}</h2>
              </div>
              <button onClick={() => setHelpOpen(false)}
                className="size-7 inline-flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground">
                <X className="h-4 w-4"/>
              </button>
            </div>
            <div className="max-h-[68vh] overflow-y-auto px-2 py-2 space-y-3">
              {(['nav', 'actions', 'global'] as const).map(group => {
                const items = SHORTCUTS.filter(s => s.group === group);
                if (!items.length) return null;
                return (
                  <div key={group}>
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                      {t(`groups.${group}`)}
                    </div>
                    <div className="space-y-0.5">
                      {items.map((s, i) => {
                        const Icon = s.icon;
                        return (
                          <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-accent/30">
                            {Icon && <Icon size={14} className="text-muted-foreground shrink-0"/>}
                            <span className="text-sm text-foreground/90 flex-1">{s.description}</span>
                            <span className="flex items-center gap-1 font-mono">
                              {s.keys.map((k, ki) => (
                                <React.Fragment key={ki}>
                                  {ki > 0 && <span className="text-muted-foreground text-[10px]">{t('then')}</span>}
                                  <kbd className="px-1.5 py-0.5 text-[10px] rounded border border-border bg-muted text-foreground/90 shadow-sm">
                                    {k}
                                  </kbd>
                                </React.Fragment>
                              ))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-border px-4 py-2 text-[10px] text-muted-foreground flex items-center justify-between font-mono">
              <span><kbd className="px-1 rounded bg-muted">?</kbd> {t('footer.open')}</span>
              <span><kbd className="px-1 rounded bg-muted">esc</kbd> {t('footer.close')}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KeyboardShortcuts;
